'use client';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import Image from '@repo/ui/src/components/Image';
import Section from '@repo/ui/src/components/Section';
import { useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { imageFullWidthSmAndUp } from '@style/variables.js';
import { MediaType } from '@repo/ui/src/types/image';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { ToutContent as BaseToutContent } from '@repo/ui/src/components/ToutContent';
import { MetaProps } from '@repo/ui/src/types';

interface PelletImageProps {
  endpoint: string;
  name: string;
}

export interface Props {
  toutContent: ToutContentProps;
  heroImage: {
    image: MediaType;
    static: boolean;
    _meta: MetaProps;
  };
}

const FuelHero = ({ toutContent, heroImage }: Props) => {
  const ref = useRef<HTMLElement>(null);
  const pelletRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  const heroImageUrl = buildImageSrc(heroImage, imageFullWidthSmAndUp, 10, 4);
  const pelletImage: PelletImageProps = {
    endpoint: 'traeger',
    name: 'pellets-warm',
  };
  const pelletImageUrl = buildImageSrc(pelletImage, 'auto', 0, 0);
  const HeaderEl = toutContent?.headerVariation?.headerVariation || 'h2';

  useEffect(() => {
    if (reduceMotion) return;

    const element = ref.current;
    const pelletElement = pelletRef.current;

    if (element && pelletElement) {
      gsap.set(pelletElement, {
        yPercent: 0,
      });

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: 'top bottom',
          end: 'bottom 25%',
          scrub: 1,
          immediateRender: true,
          invalidateOnRefresh: true,
        },
      });

      timeline.fromTo(pelletElement, { yPercent: 0 }, { yPercent: -35 });

      return () => {
        timeline.kill();
      };
    }
  }, [reduceMotion]);

  return (
    <Section
      as="section"
      texture
      rootRef={ref}
      className="tw-pb-12 sm:tw-pt-20 sm:tw-pb-15 md:tw-pt-[30vh] md:tw-pb-20"
      zIndexNone
    >
      <div
        data-testId="fuel-hero"
        ref={pelletRef}
        className="tw-h-full tw-mix-blend-multiply tw-w-3/4 tw-absolute tw-left-1/2 tw-top-0 tw-opacity-70 tw-transform tw-translate-x-[-50%] tw-translate-y-0 sm:tw-w-3/5 md:tw-w-1/2 lg:tw-w-[43%]"
      >
        <Image src={pelletImageUrl} layout="fill" alt="" role="presentation" />
      </div>
      <div
        className="tw-relative tw-flex tw-flex-col tw-justify-center tw-items-center tw-z-1 tw-bg-cover tw-bg-no-repeat tw-bg-center tw-py-20 tw-px-5 tw-mb-12 sm:tw-mb-20 sm:tw-py-36"
        style={{ backgroundImage: `url("${heroImageUrl}")` }}
      >
        {(toutContent.headingLight || toutContent.heading) && (
          <HeaderEl className="flexible tw-text-warm-100 tw-text-center tra-text-web-display-1">
            {toutContent.headingLight && (
              <>
                <span className="tw-block tw-font-display46">{toutContent.headingLight}</span>
                {/* Add spaces for SEO */}
                <span className="tw-sr-only">&nbsp;</span>
              </>
            )}
            <span className="">{toutContent.heading}</span>
          </HeaderEl>
        )}
      </div>
      <BaseToutContent
        align="center"
        fullWidth
        description={toutContent.description}
        buttons={toutContent.buttons}
        className="tw-px-10 md:tw-px-gutterLG"
      />
    </Section>
  );
};

export default FuelHero;
